/**
 * Ajustes — Personalización del panel + información del negocio.
 *
 * 3 tabs:
 *   - Apariencia → tema (light/dark/system) + color principal de la marca.
 *   - Negocio    → datos de la tienda (nombre, hostname, currency, locale).
 *   - Módulos    → toggles de loyalty/blog/reviews/affiliates/whatsapp.
 *
 * El tema se persiste en localStorage (preferencia visual del usuario).
 * Los demás cambios viven en memoria, igual que el resto del demo.
 */
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Card, CardContent, Input, Label, Button, Badge, PageHeader,
} from '@/components/ui'
import { adminApi } from '@/lib/api'
import { useTheme, PRIMARY_PALETTE, type ThemeMode } from '@/lib/theme-store'
import {
  Palette, Building2, Puzzle, Sun, Moon, Monitor, Check, Save,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/settings')({
  component: StoreSettingsPage,
})

interface Tenant {
  id: string
  slug: string
  name: string
  primaryHostname: string | null
  status: string
  defaultCurrency: string
  defaultLocale: string
  branding: Record<string, unknown>
  metadata: Record<string, string>
}

type Tab = 'appearance' | 'business' | 'modules'

function StoreSettingsPage() {
  const { id } = Route.useParams()
  const [tab, setTab] = useState<Tab>('appearance')

  return (
    <>
      <PageHeader title="Ajustes" description="Personalización del panel y configuración del negocio" />
      <div className="p-8 space-y-6">
        <div className="flex items-center gap-1 border-b border-border">
          <TabBtn active={tab === 'appearance'} onClick={() => setTab('appearance')}><Palette className="size-3.5" /> Apariencia</TabBtn>
          <TabBtn active={tab === 'business'}   onClick={() => setTab('business')}><Building2 className="size-3.5" /> Negocio</TabBtn>
          <TabBtn active={tab === 'modules'}    onClick={() => setTab('modules')}><Puzzle className="size-3.5" /> Módulos</TabBtn>
        </div>

        {tab === 'appearance' && <AppearanceTab />}
        {tab === 'business'   && <BusinessTab tenantId={id} />}
        {tab === 'modules'    && <ModulesTab tenantId={id} />}
      </div>
    </>
  )
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
        active
          ? 'border-[hsl(var(--primary))] text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}

// ============================================================
// Tab: Apariencia
// ============================================================

function AppearanceTab() {
  const { mode, primaryHsl, primaryHex, setMode, setPrimary } = useTheme()
  const [customHex, setCustomHex] = useState(primaryHex)

  const applyCustomHex = () => {
    const hex = customHex.trim()
    if (!/^#?[0-9a-f]{6}$/i.test(hex)) return
    const clean = hex.startsWith('#') ? hex : `#${hex}`
    const hsl = hexToHsl(clean)
    if (!hsl) return
    setPrimary(hsl, clean)
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardContent className="p-6 space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Modo del panel</h3>
              <p className="text-xs text-muted-foreground mt-1">Elegí cómo se ve el admin para tu equipo.</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <ModeCard active={mode === 'light'}  onClick={() => setMode('light')}  icon={<Sun     className="size-5" />} label="Claro"   description="Fondo blanco, texto negro." />
              <ModeCard active={mode === 'dark'}   onClick={() => setMode('dark')}   icon={<Moon    className="size-5" />} label="Oscuro"  description="Fondo negro, texto blanco." />
              <ModeCard active={mode === 'system'} onClick={() => setMode('system')} icon={<Monitor className="size-5" />} label="Sistema" description="Sigue tu OS." />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Color principal</h3>
              <p className="text-xs text-muted-foreground mt-1">Se aplica a botones, badges, links activos y resaltados.</p>
            </div>
            <div className="grid grid-cols-6 sm:grid-cols-12 gap-2">
              {PRIMARY_PALETTE.map((c) => {
                const selected = c.hsl === primaryHsl
                return (
                  <button
                    key={c.id}
                    title={c.label}
                    onClick={() => { setPrimary(c.hsl, c.hex); setCustomHex(c.hex) }}
                    className={`aspect-square rounded-md transition flex items-center justify-center text-foreground ${selected ? 'ring-2 ring-offset-2 ring-offset-[hsl(var(--background))] ring-foreground' : ''}`}
                    style={{ background: c.hex }}
                  >
                    {selected && <Check className="size-4" />}
                  </button>
                )
              })}
            </div>
            <div className="border-t border-border pt-4">
              <Label className="block mb-2">Color personalizado</Label>
              <div className="flex items-center gap-2">
                <Input value={customHex} onChange={(e) => setCustomHex(e.target.value)} placeholder="#f97316" className="font-mono" />
                <input
                  type="color"
                  value={customHex.startsWith('#') ? customHex : `#${customHex}`}
                  onChange={(e) => setCustomHex(e.target.value)}
                  className="h-9 w-12 rounded border border-border bg-transparent cursor-pointer"
                />
                <Button onClick={applyCustomHex}>Aplicar</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live preview */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Vista previa</h3>
          <p className="text-xs text-muted-foreground -mt-2">Lo que ven tus usuarios.</p>

          <div className="space-y-3">
            <Button className="w-full">Botón principal</Button>
            <Button variant="outline" className="w-full">Botón secundario</Button>
            <Button variant="ghost" className="w-full">Botón fantasma</Button>
          </div>

          <div className="border-t border-border pt-4 space-y-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Badges</p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="primary">Primary</Badge>
              <Badge variant="success">Success</Badge>
              <Badge variant="warning">Warning</Badge>
              <Badge variant="destructive">Error</Badge>
              <Badge variant="muted">Muted</Badge>
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Input</p>
            <Input placeholder="Tu nombre" />
          </div>

          <div className="border-t border-border pt-4 text-xs text-muted-foreground">
            Color activo: <code className="text-foreground">{primaryHex}</code>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function ModeCard({ active, onClick, icon, label, description }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; description: string
}) {
  return (
    <button
      onClick={onClick}
      className={`text-left p-4 rounded-lg border transition ${
        active ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.05)]' : 'border-border hover:border-muted-foreground'
      }`}
    >
      <div className={active ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground'}>{icon}</div>
      <p className="font-medium text-foreground mt-2">{label}</p>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </button>
  )
}

// ============================================================
// Tab: Negocio
// ============================================================

function BusinessTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => adminApi.get<{ data: Tenant }>(`/api/admin/tenants/${tenantId}`),
  })
  const [draft, setDraft] = useState<Partial<Tenant>>({})
  const [saved, setSaved] = useState(false)
  const t = data?.data
  const v = (k: keyof Tenant) => (draft[k] !== undefined ? (draft[k] as string) : ((t?.[k] as string) ?? ''))

  const dirty = Object.keys(draft).length > 0
  const save = async () => {
    await adminApi.patch(`/api/admin/tenants/${tenantId}`, draft)
    setDraft({})
    setSaved(true)
    setTimeout(() => setSaved(false), 1800)
    qc.invalidateQueries({ queryKey: ['tenant', tenantId] })
  }

  if (!t) return <p className="text-sm text-muted-foreground">Cargando…</p>

  return (
    <Card>
      <CardContent className="p-6 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Información del negocio</h3>
          <p className="text-xs text-muted-foreground mt-1">Datos que aparecen en facturas, emails y storefront.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Nombre del negocio</Label><Input value={v('name')} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
          <div><Label>Slug (URL)</Label><Input value={v('slug')} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>Hostname principal</Label><Input value={v('primaryHostname') ?? ''} onChange={(e) => setDraft({ ...draft, primaryHostname: e.target.value })} placeholder="demo-store.skeleton-store.com" /></div>
          <div>
            <Label>Moneda por defecto</Label>
            <select
              value={v('defaultCurrency')}
              onChange={(e) => setDraft({ ...draft, defaultCurrency: e.target.value })}
              className="w-full bg-[hsl(var(--elevated))] border border-border rounded px-3 py-2 text-sm text-foreground"
            >
              <option value="COP">COP — Peso colombiano</option>
              <option value="USD">USD — Dólar estadounidense</option>
              <option value="MXN">MXN — Peso mexicano</option>
              <option value="ARS">ARS — Peso argentino</option>
              <option value="CLP">CLP — Peso chileno</option>
              <option value="EUR">EUR — Euro</option>
            </select>
          </div>
          <div>
            <Label>Idioma por defecto</Label>
            <select
              value={v('defaultLocale')}
              onChange={(e) => setDraft({ ...draft, defaultLocale: e.target.value })}
              className="w-full bg-[hsl(var(--elevated))] border border-border rounded px-3 py-2 text-sm text-foreground"
            >
              <option value="es-CO">Español (Colombia)</option>
              <option value="es-MX">Español (México)</option>
              <option value="es-AR">Español (Argentina)</option>
              <option value="en-US">English (US)</option>
              <option value="pt-BR">Português (Brasil)</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
          {saved && <span className="text-xs text-green-700 dark:text-green-400 inline-flex items-center gap-1"><Check className="size-3.5" /> Guardado</span>}
          <Button variant="ghost" disabled={!dirty} onClick={() => setDraft({})}>Descartar</Button>
          <Button disabled={!dirty} onClick={save}><Save className="size-3.5 mr-1.5" /> Guardar</Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================
// Tab: Módulos
// ============================================================

function ModulesTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => adminApi.get<{ data: Tenant }>(`/api/admin/tenants/${tenantId}`),
  })
  const t = data?.data
  const enabled = (mod: string) => (t?.metadata?.modules ?? '').includes(mod)
  const toggle = async (mod: string) => {
    if (!t) return
    const current = (t.metadata?.modules ?? '').split(',').filter(Boolean)
    const next = current.includes(mod) ? current.filter((m) => m !== mod) : [...current, mod]
    await adminApi.patch(`/api/admin/tenants/${tenantId}`, {
      metadata: { ...t.metadata, modules: next.join(',') },
    })
    qc.invalidateQueries({ queryKey: ['tenant', tenantId] })
  }

  const modules = [
    { id: 'loyalty',    label: 'Fidelización',   description: 'Tiers, códigos canjeables y miembros.' },
    { id: 'blog',       label: 'Blog',           description: 'Artículos editoriales y SEO.' },
    { id: 'reviews',    label: 'Reviews',        description: 'Reseñas con rating de productos.' },
    { id: 'affiliates', label: 'Afiliados',      description: 'Programa de referidos con comisiones.' },
    { id: 'whatsapp',   label: 'WhatsApp CRM',   description: 'Bandeja unificada de conversaciones.' },
    { id: 'agents',     label: 'Agentes IA',     description: 'Atención automatizada con IA.' },
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {modules.map((m) => {
        const on = enabled(m.id)
        return (
          <Card key={m.id} className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-foreground">{m.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{m.description}</p>
              </div>
              <Toggle checked={on} onChange={() => toggle(m.id)} />
            </div>
          </Card>
        )
      })}
    </div>
  )
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${checked ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'}`}
    >
      <span className={`inline-block size-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  )
}

// ============================================================
// utils
// ============================================================

function hexToHsl(hex: string): string | null {
  const h = hex.replace('#', '')
  if (h.length !== 6) return null
  const r = parseInt(h.slice(0, 2), 16) / 255
  const g = parseInt(h.slice(2, 4), 16) / 255
  const b = parseInt(h.slice(4, 6), 16) / 255
  if ([r, g, b].some((v) => Number.isNaN(v))) return null
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let hh = 0, s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break
      case g: hh = (b - r) / d + 2; break
      case b: hh = (r - g) / d + 4; break
    }
    hh /= 6
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}
